<script setup lang="ts">
const { locale } = useI18n()

const emit = defineEmits<{ loaded: [] }>()

const ENTRIES_LIMIT = 200

interface PersonOccurrenceEntry {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  type: 'participant' | 'tutor' | 'staff'
  person?: { id: string; slug: string }
}

interface GuestPerson {
  id: string
  firstName: string
  lastName: string
  isExternal: boolean
  person?: { id: string; slug: string }
}

interface GuestDoc {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  people: GuestPerson[]
}

// the complement of the current list: everything that ended before today
const now = new Date()
const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString()

const occurrencesWhere = {
  dateEnd: { less_than: startOfToday },
  type: { in: ['participant', 'tutor', 'staff'] },
}

const guestsWhere = {
  dateEnd: { less_than: startOfToday },
}

// cached globally (per locale) so the archive only needs to fetch once per site visit,
// no matter how many times it's toggled open/closed
const archiveState = useState(`people-archive-data-${locale.value}`, () => ({
  occurrences: [] as PersonOccurrenceEntry[],
  guests: [] as GuestDoc[],
  loaded: false,
}))

const fetchAllPages = async <T,>(
  query: string,
  where: Record<string, unknown>
): Promise<T[]> => {
  let page = 1
  let all: T[] = []
  while (true) {
    const result = await $fetch<{ docs: T[]; hasNextPage: boolean }>('/api/payload-gql', {
      method: 'POST',
      body: {
        query,
        variables: { page, limit: ENTRIES_LIMIT, locale: locale.value, where },
      },
    })
    all = [...all, ...(result?.docs ?? [])]
    if (!result?.hasNextPage) break
    page++
  }
  return all
}

onMounted(async () => {
  if (archiveState.value.loaded) {
    emit('loaded')
    return
  }
  const [occurrences, guests] = await Promise.all([
    fetchAllPages<PersonOccurrenceEntry>(ManyPersonOccurrencesQuery, occurrencesWhere),
    fetchAllPages<GuestDoc>(ManyGuestsQuery, guestsWhere),
  ])
  archiveState.value = { occurrences, guests, loaded: true }
  emit('loaded')
})

const allOccurrences = computed(() => archiveState.value.occurrences)
const allGuests = computed(() => archiveState.value.guests)

const previewRef = ref<{ yearLabels: string[]; scrollToYear: (yearLabel: string) => void } | null>(null)

const yearRange = computed(() => {
  const allDates = [...allOccurrences.value.map(o => o.dateStart), ...allOccurrences.value.map(o => o.dateEnd)]
  const years = allDates.map(d => new Date(d).getUTCFullYear())
  const minYear = Math.min(...years)
  const maxYear = Math.max(...years)
  return { minYear, maxYear }
})
</script>

<template>
  <div>
    <h1 class="mb-2 text-md"> 
      {{ $t('pages.people.archive.title') }} {{ yearRange?.minYear }}–{{ yearRange?.maxYear }}
    </h1>

    <pages-people-archive-preview
      ref="previewRef"
      :entries="allOccurrences"
      :guests="allGuests"
    />
  </div>
</template>
